import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetServiceCostPrices, useGetTourServices } from '~/hooks/apis/tours/useTourCostPrices';

import { FixedCostPriceItem, SlidingScalePricingItem } from '~/types/tours/costPrice';
import { Supplier, costPriceTypeLabel, servicePricing } from '~/types/tours/supplier';

import ServiceFormDialog from '../../Suppliers/ServiceFormDialog';
import SupplierPickerDialog from '../../Suppliers/SupplierPickerDialog';
import TourDetailsContext from '../TourDetailsContext';

import FixedPriceDialog from './FixedPriceDialog';
import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import SlidingScaleUploadDialog from './SlidingScaleUploadDialog';

interface Props {
  tourId: string;
}

const SERVICE_ID_PARAM = 'serviceId';

export default function TourSlidingScale({ tourId }: Props) {
  const { tourDetails } = useContext(TourDetailsContext);

  const [searchParams, setSearchParams] = useSearchParams();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isFixedPriceOpen, setIsFixedPriceOpen] = useState(false);

  // Two steps, because creating the service is what attaches the tour.
  const [linkStep, setLinkStep] = useState<'supplier' | 'service' | null>(null);
  const [linkedSupplier, setLinkedSupplier] = useState<Supplier | null>(null);

  const { data: services = [], isLoading: isLoadingServices, error: servicesError } = useGetTourServices(tourId);

  /**
   * The chosen service is kept in the URL so the view can be linked and
   * survives a reload. It defaults to the tour's only service when there is
   * just one, which is the common case.
   */
  const selectedServiceId = searchParams.get(SERVICE_ID_PARAM) ?? (services.length === 1 ? services[0].id : '');

  const handleServiceChange = useCallback(
    (serviceId: string) => {
      const next = new URLSearchParams(searchParams);

      if (serviceId) {
        next.set(SERVICE_ID_PARAM, serviceId);
      } else {
        next.delete(SERVICE_ID_PARAM);
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) ?? null,
    [services, selectedServiceId],
  );

  // Cost prices are keyed by supplier and service, both taken from the
  // selected service.
  const serviceIdentifier = useMemo(
    () => ({ supplierId: selectedService?.supplierId, serviceId: selectedService?.id }),
    [selectedService],
  );

  const { data, isLoading, error } = useGetServiceCostPrices(serviceIdentifier);

  const pricing = useMemo<Array<SlidingScalePricingItem>>(
    () => (data && 'slidingScale' in data ? data.slidingScale : []),
    [data],
  );
  const fixedPrices = useMemo<Array<FixedCostPriceItem>>(() => (data && 'fixed' in data ? data.fixed : []), [data]);

  // Fixed prices are quoted in the same currency as the rest of the service, so
  // an existing sliding scale is the best available default.
  const supplierCurrency = pricing[0]?.currency;

  // Offer the seasons the service already prices in, so a fixed price does not
  // introduce a near duplicate like "low" alongside "Low".
  const seasonOptions = useMemo(() => Array.from(new Set(pricing.map((item) => item.season))).sort(), [pricing]);

  /**
   * Package option and currency pairs already holding a sliding scale, so the
   * fixed price dialog can warn that saving replaces the seasons it sets.
   */
  const slidingScalePairs = useMemo(() => {
    if (!selectedService) {
      return [];
    }

    return pricing.reduce<Array<string>>((pairs, item) => {
      const configuration = selectedService.configurations.find(
        (candidate) => candidate.name === item.tourConfiguration,
      );
      const packageConfiguration = configuration?.packages.find(
        (candidate) => candidate.name === item.packageConfiguration,
      );

      return packageConfiguration ? pairs.concat(`${packageConfiguration.id}::${item.currency}`) : pairs;
    }, []);
  }, [pricing, selectedService]);

  // With no service there is nothing to price, so those actions are left out
  // rather than shown disabled.
  const hasService = !!selectedService;

  const pricingActions = selectedService ? servicePricing(selectedService) : null;

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <PageHeader title="Sliding scale">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {hasService ? (
            <>
              {pricingActions.canUploadSlidingScale && (
                <Button variant="contained" onClick={() => setIsUploadOpen(true)}>
                  {pricing.length ? 'Re-upload sliding scale' : 'Upload sliding scale'}
                </Button>
              )}
              {pricingActions.canSetFixedPrice && (
                <Button variant="contained" onClick={() => setIsFixedPriceOpen(true)}>
                  Set fixed price
                </Button>
              )}
              <Button variant="text" onClick={() => setLinkStep('supplier')}>
                Link another service
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setLinkStep('supplier')}>
              Link supplier &amp; service
            </Button>
          )}
        </Stack>
      </PageHeader>

      {isLoadingServices && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!servicesError && <Alert severity="error">{servicesError.message}</Alert>}

      {!isLoadingServices && !servicesError && !services.length && (
        <Alert severity="info">
          This tour has no supplier services yet, so there is nowhere to attach a cost price. Link a supplier and create
          a service for the tour to start pricing it.
        </Alert>
      )}

      {!!services.length && (
        <TextField
          select
          label="Service"
          value={selectedServiceId}
          onChange={(event) => handleServiceChange(event.target.value)}
          helperText={
            selectedService?.costPriceType
              ? `Costed as ${costPriceTypeLabel(selectedService.costPriceType).toLowerCase()}, priced ${
                  selectedService.costPriceScope === 'Departure' ? 'per departure' : 'for the whole tour'
                }`
              : "Cost prices belong to a supplier's service on this tour"
          }
          sx={{ maxWidth: 560 }}
        >
          {services.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name} — {service.kind}
            </MenuItem>
          ))}
        </TextField>
      )}

      {!!pricingActions?.hasNoPricingPath && (
        <Alert severity="warning">
          This service is priced per departure on a sliding scale, which has no upload yet - svc-tour accepts the file
          and stores nothing. Set the service to price the whole tour to upload a scale against it.
        </Alert>
      )}

      {!!selectedService && isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!selectedService && !!error && <Alert severity="error">{error.message}</Alert>}

      {!!selectedService && !isLoading && !error && (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={600}>
            Cost prices
          </Typography>
          <SlidingScaleTable pricing={pricing} />
          <FixedPriceTable prices={fixedPrices} />
        </Stack>
      )}

      {isUploadOpen && !!selectedService && (
        <SlidingScaleUploadDialog
          open={isUploadOpen}
          service={selectedService}
          existingRowCount={pricing.length}
          onClose={() => setIsUploadOpen(false)}
        />
      )}

      {isFixedPriceOpen && !!selectedService && (
        <FixedPriceDialog
          open={isFixedPriceOpen}
          service={selectedService}
          defaultCurrency={supplierCurrency}
          seasonOptions={seasonOptions}
          slidingScalePairs={slidingScalePairs}
          onClose={() => setIsFixedPriceOpen(false)}
        />
      )}

      {linkStep === 'supplier' && (
        <SupplierPickerDialog
          open
          title="Link a supplier"
          description="Cost prices belong to a supplier's service. Choose the supplier this tour is bought from, then create its service for the tour."
          onClose={() => setLinkStep(null)}
          onSelect={(supplier) => {
            setLinkedSupplier(supplier);
            setLinkStep('service');
          }}
        />
      )}

      {linkStep === 'service' && !!linkedSupplier && (
        <ServiceFormDialog
          open
          supplierId={linkedSupplier.id}
          tour={{ id: tourId, name: tourDetails?.title ?? tourId }}
          onClose={() => setLinkStep(null)}
          onCreated={(service) => handleServiceChange(service.id)}
        />
      )}
    </Stack>
  );
}
